import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  cn,
  Switch,
} from '@herit-fe/heritage-ui';

export interface SwitchGroupOption {
  value: string;
  label: string;
  /** 라벨 아래 보조 설명(선택). */
  description?: string;
  disabled?: boolean;
  /** 행 단위 스타일(선택) — 예: '전체' 마스터 행 배경 강조(`MultiSelectAllField`). 목록 안 위계 표현용. */
  className?: string;
}

export interface SwitchGroupProps {
  /** 옵션 — 각 행이 독립 on/off. */
  options: SwitchGroupOption[];
  /** on(선택)된 값. */
  value: string[];
  onValueChange: (value: string[]) => void;
  /** 그룹 a11y 라벨(`FieldLabel` id 결합) — 비-collapsible 일 때. */
  'aria-labelledby'?: string;
  className?: string;
  /** 접기/펴기 가능 — heritage `Accordion`(밑줄형 `AccordionTrigger` + 우측 chevron) 헤더를 자체 렌더(옵션 多 시 공간 절약). `label` 이 그룹 라벨. */
  collapsible?: boolean;
  /** collapsible 헤더에 표시할 그룹명(= 라벨, `FieldLabel` 대신). */
  label?: string;
  /** collapsible 초기 펼침 여부(기본 true). */
  defaultExpanded?: boolean;
}

// 단일 그룹 accordion 의 고정 item value — `defaultValue` 와 짝(초기 열림 여부 매핑용).
const ACCORDION_ITEM = 'switchgroup';

/**
 * 독립 on/off 옵션 N개 — 행마다 라벨(+설명) ↔ `Switch`. "boolean=Switch" 규칙의 multi 일반화(multi-boolean).
 * 무상태(value/onValueChange 주입). 값은 `options` 순서로 정규화. 드롭다운 팝업이 없어 시트/오버레이와 충돌 없음.
 * `collapsible` 이면 heritage `Accordion`(`AccordionTrigger` = 라벨 + hover 밑줄 + 우측 chevron)으로 긴 목록을
 * 접을 수 있다(펼침 상태는 Accordion 이 보유, `defaultExpanded` = 초기 open).
 */
export function SwitchGroup({
  options,
  value,
  onValueChange,
  className,
  'aria-labelledby': ariaLabelledby,
  collapsible = false,
  label,
  defaultExpanded = true,
}: SwitchGroupProps) {
  const selected = new Set(value);
  const handleToggle = (optionValue: string, on: boolean) => {
    const next = new Set(selected);
    if (on) next.add(optionValue);
    else next.delete(optionValue);
    onValueChange(options.filter((option) => next.has(option.value)).map((option) => option.value));
  };

  /**
   * @description data-drawer-content, 드로워 내부에서 스위치 작동이 불가한 문제 수정
   */
  const rows = options.map((option) => (
    <div
      key={option.value}
      className={cn('flex items-center justify-between gap-3 px-3 py-2.5', option.className)}
    >
      <span className="flex flex-col">
        <span className="text-sm text-foreground">{option.label}</span>
        {option.description && (
          <span className="text-xs text-muted-foreground">{option.description}</span>
        )}
      </span>
      <Switch
        data-drawer-content
        checked={selected.has(option.value)}
        onCheckedChange={(on) => handleToggle(option.value, on)}
        disabled={option.disabled}
        aria-label={option.label}
      />
    </div>
  ));

  // collapsible — heritage `Accordion`(DS 표준). 트리거 = 라벨 + hover 밑줄 + 우측 chevron(펼침 시 up).
  // 단일 그룹이라 AccordionItem 1개 + 펼친 패널에 스위치 목록(bordered box). label 이 그룹 라벨(FieldLabel 대체).
  if (collapsible) {
    return (
      <Accordion
        defaultValue={defaultExpanded ? [ACCORDION_ITEM] : []}
        aria-label={label}
        className={className}
      >
        <AccordionItem value={ACCORDION_ITEM}>
          {/* pr-2 = SelectField chevron(absolute right-2)과 동일 우측 inset 으로 chevron 정렬. */}
          <AccordionTrigger className="pr-2">{label}</AccordionTrigger>
          <AccordionContent>
            <div className="flex flex-col divide-y divide-border overflow-hidden rounded-md border border-border bg-card">
              {rows}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    );
  }

  return (
    <div
      role="group"
      aria-labelledby={ariaLabelledby}
      // 템플릿 리터럴 대신 `cn`(twMerge) — 소비처가 `ELEVATED_SURFACE`(border-0 + drop-shadow) 로 **테두리를
      // 덮어쓸 수 있어야** 한다. 문자열 이어붙이기는 충돌 시 CSS 생성 순서에 좌우돼 결과가 불안정하다.
      className={cn(
        'flex flex-col divide-y divide-border overflow-hidden rounded-md border border-border bg-card',
        className,
      )}
    >
      {rows}
    </div>
  );
}
